import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  HiOutlineMail,
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeOff,
} from 'react-icons/hi';
import krishnaLogo from '../../assets/krishnalogo.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white lg:bg-slate-50">
      <div className="hidden lg:flex lg:w-1/2 p-10 xl:p-12 flex-col justify-center items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#425F97] via-[#4f6fb0] to-[#7f9bd6] opacity-90" />
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-20 w-80 h-80 rounded-3xl bg-white/10 rotate-12 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.16),_transparent_55%)]" />

        <div className="relative z-10 flex flex-col items-center justify-center text-center">
          <div className="bg-white/95 rounded-2xl p-6 xl:p-8 shadow-2xl mb-5 xl:mb-6">
            <img
              src={krishnaLogo}
              alt="Krishna Pediatric Rehabilitation Clinic"
              className="h-32 w-auto object-contain"
            />
          </div>
          <h1 className="text-2xl xl:text-3xl font-bold text-white tracking-[0.2em] xl:tracking-[0.25em] mb-1">KRISHNA</h1>
          <p className="text-white/90 text-base xl:text-lg">Pediatric Rehabilitation Clinic</p>
          <p className="mt-4 max-w-md text-base font-bold text-white/80">
            "Adding Life to Years, Not Years to Life."
          </p>
        </div>
      </div>

      <div className="flex min-h-[100dvh] flex-1 flex-col bg-white lg:min-h-0 lg:items-center lg:justify-center lg:bg-transparent">
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6 lg:block lg:flex-none lg:px-8 lg:py-8 lg:pb-8 lg:pt-8">
          <header className="shrink-0 text-center lg:hidden">
            <img
              src={krishnaLogo}
              alt="Krishna Pediatric Rehabilitation Clinic"
              className="mx-auto h-[4.75rem] w-auto max-w-[min(90vw,19rem)] object-contain sm:h-[5.5rem]"
            />
            <div
              className="mx-auto mt-6 h-1 w-14 rounded-full bg-[#425F97]/20 sm:mt-7"
              aria-hidden
            />
          </header>

          <div className="mt-6 flex-1 lg:mt-0 lg:flex-none rounded-2xl border border-gray-100 bg-white px-4 py-6 shadow-[0_2px_12px_rgba(15,23,42,0.06)] sm:px-6 sm:py-7 lg:rounded-none lg:border-0 lg:bg-transparent lg:px-0 lg:py-0 lg:shadow-none">
            <div className="mb-6 text-center sm:mb-8 lg:mb-8">
              <h2 className="font-display text-[1.375rem] font-bold leading-tight text-gray-900 sm:text-2xl">
                Welcome back
              </h2>
              <p className="mx-auto mt-2 max-w-[20rem] text-sm leading-relaxed text-gray-500 sm:max-w-none sm:text-[15px]">
                Sign in to manage your pediatric rehab patients and schedules.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-5">
              <div>
                <label className="label">Email</label>
                <div className="relative">
                  <HiOutlineMail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-10"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="label">Password</label>
                <div className="relative">
                  <HiOutlineLockClosed className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10 pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 min-h-[40px] min-w-[40px] flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  >
                    {showPassword ? (
                      <HiOutlineEyeOff className="w-5 h-5" />
                    ) : (
                      <HiOutlineEye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex w-full min-h-[52px] items-center justify-center rounded-xl bg-[#425F97] py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-[#384f7d] focus:outline-none focus:ring-2 focus:ring-[#425F97] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 lg:min-h-[48px] lg:rounded-lg lg:py-3 lg:font-medium lg:shadow-none"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
